/**
 * Inline hint under a bank picker when a published bank still has unpublished
 * questions. Editors get a one-click "Publish all N items"; others see a hint.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';

export function BankUnpublishedHint({ bank, onPublished }) {
  const { post } = useApi();
  const { hasRole } = useAuth();
  const [busy, setBusy] = useState(false);
  const n = bank?.unpublished_item_count || 0;
  if (!bank || n === 0) return null;
  const canEdit = hasRole('editor');

  const publish = async () => {
    setBusy(true);
    try {
      const updated = await post(`/question-banks/${bank.bank_id}/publish-items`, {});
      toast.success(`${n} question${n === 1 ? '' : 's'} published`);
      onPublished?.(updated);
    } catch (e) { toast.error(e.response?.data?.detail || 'Publish failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center justify-between gap-2 text-xs border rounded-md px-2.5 py-1.5"
         data-testid="bank-unpublished-hint">
      <span className="text-muted-foreground">
        {n} unpublished question{n === 1 ? '' : 's'}{canEdit ? ' — not drawn until published.' : ' — ask an editor to publish them.'}
      </span>
      {canEdit && (
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={publish} disabled={busy}
                data-testid="bank-publish-all-inline-btn">
          <CheckCircle2 className="w-3 h-3 mr-1" /> {busy ? 'Publishing…' : `Publish all ${n} item${n === 1 ? '' : 's'}`}
        </Button>
      )}
    </div>
  );
}
